// feature/litisoft-export — collapsible "Datos del siniestro" panel.
// Used in NewCase and LateEntry intake; also re-rendered in CaseDetail
// General tab as an editable inline form.
//
// Sprint 5 introduced 6 fields (fecha_siniestro, placa, numero_siniestro,
// departamento, municipio, tematica) for the LITISOFT export.
// P2.5 extends with 4 more (poliza_soat, accionado_previsora,
// vinculados, vinculado_principal) for the Previsora REMANENTE export.
// All 10 live in the same panel — the team thinks of them as one
// concept ("datos del siniestro") regardless of which Excel they
// eventually feed.
import React, { useState } from "react";

export const DEPARTAMENTOS = [
  "Amazonas","Antioquia","Arauca","Atlántico","Bogotá D.C.","Bolívar","Boyacá",
  "Caldas","Caquetá","Casanare","Cauca","Cesar","Chocó","Córdoba","Cundinamarca",
  "Guainía","Guaviare","Huila","La Guajira","Magdalena","Meta","Nariño",
  "Norte de Santander","Putumayo","Quindío","Risaralda","San Andrés y Providencia",
  "Santander","Sucre","Tolima","Valle del Cauca","Vaupés","Vichada",
];

export const TEMATICAS = [
  "Pérdida de Capacidad Laboral (PCL)",
  "Honorarios Junta de Calificación",
  "Indemnización por Incapacidad Permanente",
  "Derecho de Petición - Calificación PCL",
  "Tutela contra EPS",
  "Tratamiento médico",
];

// sprint83 — 3 new LITISOFT enum fields. Keep in sync with
// eds-backend/lib/litisoft_enums.js (port-by-port — duplicating because
// the frontend can't require from backend). If a value is changed/added
// on either side, change both.
export const PRETENSION_VALUES = [
  "ACCIDENTES PERSONALES", "ACTUACION TEMERARIA", "COMPENSACIÓN",
  "CONSULTA PREVIA", "CONTRA PROVIDENCIA (PROCESO FISCAL)",
  "CONTRA PROVIDENCIA JUDICIAL", "CONTRATO DE SEGUROS",
  "DERECHO DE PETICIÓN", "DESVINCULACIÓN", "EMBARGOS",
  "ESTABILIDAD LABORAL REFORZADA",
  "EXPEDICION DE POLIZAS DE SERIDAD CANDIDATURA NO ASEGURABLE",
  "EXPEDICION POLIZAS (PRESUNCION DE RIESGOS)", "FALTA DE LEGITIMACION",
  "FUERO SINDICAL", "HABEAS DATA", "INCAPACIDAD TEMPORAL",
  "JUNTA DE CALIFICACION", "MINIMO VITAL", "NEGACIÓN DEL SERVICIO",
  "OTROS", "PAGO DE APORTES A PENSIONES", "PAGO DE SALARIOS",
  "PENSIONES", "POLIZA VIDA GRUPO", "POR DEFINIR",
  "RECONOCIMIENTO COMUNIDADES", "REINTEGRO POR FUERO DE SALUD",
  "SEGUROS", "SOAT - AUTORIZACIONES EPS", "SOAT - PAGO INDEMNIZACIONES",
  "SUSPENSIÓN DE ACTOS ADMINISTRATIVOS", "SUSPENSIÓN DEL CONTRATO",
  "VIOLACIÓN A LA SALUD",
  // feature/pretension-seguridad-social — sprint86. Keep in lockstep with
  // eds-backend/lib/litisoft_enums.js (this list is a hand-maintained port).
  "VIOLACIÓN A LA SEGURIDAD SOCIAL",
  "VIOLACIÓN AL DEBIDO PROCESO",
  "VIOLACIÓN DEL DERECHO AL TRABAJO",
];
export const CALIDAD_PARTE_PREVISORA_VALUES = ["PARTE ACTIVA", "PARTE PASIVA", "GARANTE"];
export const TIPO_NOTIFICACION_VALUES = [
  "AVISO", "CITACION AUDIENCIA ART.86 LEY 1474",
  "CITACIÓN PARA NOTIFICACIÓN PERSONAL", "CONDUCTA CONCLUYENTE", "PERSONAL",
];

// sprint83 — only the 3 new enum fields are mandatory. The existing
// 10 LITISOFT fields stay editable in the panel but do NOT gate
// contestación. Mirrors eds-backend/lib/litisoft_enums.js.
// LITISOFT_FIELD_LABELS still covers all 13 so the incomplete-modal
// can label any field; the LITISOFT_REQUIRED_FIELDS array drives the
// asterisks + completion badge + backend gate.
export const LITISOFT_REQUIRED_FIELDS = [
  "pretension",
  "calidad_parte_previsora",
  "tipo_notificacion",
];
export const LITISOFT_FIELD_LABELS = {
  departamento:            "Departamento",
  municipio:               "Municipio / Ciudad",
  tematica:                "Temática",
  fecha_siniestro:         "Fecha del siniestro",
  placa:                   "Placa",
  numero_siniestro:        "No. Siniestro",
  poliza_soat:             "Póliza SOAT",
  accionado_previsora:     "¿Accionado a Previsora?",
  vinculados:              "Vinculados",
  vinculado_principal:     "Vinculado principal",
  pretension:              "Pretensión",
  calidad_parte_previsora: "Calidad de Parte Previsora",
  tipo_notificacion:       "Tipo de Notificación",
};
export function isLitisoftFieldMissing(value, _fieldName) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  return false;
}
export function checkLitisoftComplete(caseData) {
  const missing = LITISOFT_REQUIRED_FIELDS.filter((f) =>
    isLitisoftFieldMissing(caseData?.[f], f));
  return { complete: missing.length === 0, missing };
}

export default function LitisoftFields({
  initialOpen = false,
  values, onChange,
  // sprint83 — when truthy, render required asterisks on all 13 fields
  // and surface a header chip with the completion status. Used on case
  // detail. Intake forms can leave this false.
  showRequired = false,
  // sprint83 — array of field names to highlight in red (after the
  // user fails the contestada gate and we want to point them at the
  // gaps). Empty array = no highlight.
  highlightMissing = [],
}) {
  const [open, setOpen] = useState(initialOpen);
  const v = values || {};
  const missingSet = new Set(highlightMissing || []);
  const fieldStyle = (k) => missingSet.has(k)
    ? { outline: "2px solid var(--color-danger)", outlineOffset: 1, borderRadius: 4 }
    : undefined;
  // sprint83 — only the 3 mandatory fields get an asterisk. The other
  // 10 stay editable but optional.
  const requiredSet = new Set(LITISOFT_REQUIRED_FIELDS);
  const req = (fieldName) => (showRequired && requiredSet.has(fieldName))
    ? <span style={{ color: "var(--color-danger)", marginLeft: 2 }}>*</span>
    : null;

  // sprint83 — completion summary for the header chip. N/3 — only the
  // 3 mandatory fields count.
  const completion = showRequired ? checkLitisoftComplete(v) : null;
  const totalRequired = LITISOFT_REQUIRED_FIELDS.length;

  function set(k, val) { onChange({ ...v, [k]: val }); }

  return (
    <div className="card tight" style={{ background: "var(--bg-subtle)", borderStyle: "dashed" }}>
      <div
        className="row"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen(o => !o)}
      >
        <div>
          <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            Datos del siniestro · LITISOFT
            {completion && !completion.complete && (
              <span className="badge" style={{
                fontSize: 10, padding: "2px 8px",
                background: "var(--color-warning-bg)", color: "var(--color-warning-ink)",
                border: "1px solid var(--color-warning)", borderRadius: 10,
              }}>
                Incompleto ({totalRequired - completion.missing.length}/{totalRequired})
              </span>
            )}
            {completion && completion.complete && (
              <span className="badge" style={{
                fontSize: 10, padding: "2px 8px",
                background: "var(--color-success-bg)", color: "var(--accent-mint)",
                border: "1px solid var(--accent-mint)", borderRadius: 10,
              }}>
                Completo ✓
              </span>
            )}
          </div>
          <div className="muted" style={{ fontSize: 11 }}>
            {showRequired
              ? "3 campos (marcados con *) son obligatorios antes de marcar la contestación. Los demás también se exportan a LITISOFT pero son opcionales."
              : "Opcional al ingreso. Se usan para LITISOFT y el remanente Previsora. El equipo de carpetas puede completar luego desde el detalle del caso."}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <span aria-hidden="true" style={{ color: "var(--text-muted)", fontSize: 16 }}>{open ? "−" : "+"}</span>
      </div>

      {open && (
        <div className="grid-2" style={{ marginTop: 12 }}>
          <div>
            <label>Fecha del siniestro{req("fecha_siniestro")}</label>
            <input type="date" value={v.fecha_siniestro || ""} onChange={(e) => set("fecha_siniestro", e.target.value || null)} autoComplete="off" style={fieldStyle("fecha_siniestro")} />
          </div>
          <div>
            <label>Placa{req("placa")}</label>
            <input
              value={v.placa || ""}
              onChange={(e) => set("placa", e.target.value.toUpperCase().slice(0, 10))}
              maxLength={10}
              placeholder="ABC123"
              style={{ textTransform: "uppercase", ...(fieldStyle("placa") || {}) }}
              autoComplete="off"
            />
          </div>
          {/* P2.5 — póliza SOAT */}
          <div>
            <label>Póliza SOAT{req("poliza_soat")}</label>
            <input
              value={v.poliza_soat || ""}
              onChange={(e) => set("poliza_soat", e.target.value.slice(0, 100))}
              maxLength={100}
              placeholder="Número de póliza"
              autoComplete="off"
              style={fieldStyle("poliza_soat")}
            />
          </div>
          <div>
            <label>No. Siniestro{req("numero_siniestro")}</label>
            <input value={v.numero_siniestro || ""} onChange={(e) => set("numero_siniestro", e.target.value)} autoComplete="off" style={fieldStyle("numero_siniestro")} />
          </div>
          <div>
            <label>Departamento{req("departamento")}</label>
            <select value={v.departamento || ""} onChange={(e) => set("departamento", e.target.value || null)} style={fieldStyle("departamento")}>
              <option value="">— elegir —</option>
              {DEPARTAMENTOS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label>Municipio / Ciudad{req("municipio")}</label>
            <input value={v.municipio || ""} onChange={(e) => set("municipio", e.target.value)} autoComplete="off" style={fieldStyle("municipio")} />
          </div>
          {/* P2.5 — accionado Previsora SI/NO. Tri-state: null
              (default, "no se ha definido"), true ("Sí"), false ("No"). */}
          <div style={fieldStyle("accionado_previsora")}>
            <label>¿Accionado a Previsora?{req("accionado_previsora")}</label>
            <div style={{ display: "flex", gap: 12, padding: "6px 0" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
                <input type="radio" name="accionado_previsora" checked={v.accionado_previsora === true} onChange={() => set("accionado_previsora", true)} /> Sí
              </label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
                <input type="radio" name="accionado_previsora" checked={v.accionado_previsora === false} onChange={() => set("accionado_previsora", false)} /> No
              </label>
              {v.accionado_previsora !== null && v.accionado_previsora !== undefined && (
                <button type="button" className="ghost sm" style={{ fontSize: 11 }} onClick={() => set("accionado_previsora", null)}>Limpiar</button>
              )}
            </div>
          </div>
          {/* P2.5 — vinculado principal (single line). */}
          <div>
            <label>Vinculado principal{req("vinculado_principal")}</label>
            <input
              value={v.vinculado_principal || ""}
              onChange={(e) => set("vinculado_principal", e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder="Nombre o entidad principal vinculada"
              autoComplete="off"
              style={fieldStyle("vinculado_principal")}
            />
          </div>
          <div>
            <label>Temática{req("tematica")}</label>
            <input
              list="eds-tematicas"
              value={v.tematica || ""}
              onChange={(e) => set("tematica", e.target.value)}
              placeholder="Pérdida de Capacidad Laboral (PCL)"
              autoComplete="off"
              style={fieldStyle("tematica")}
            />
            <datalist id="eds-tematicas">
              {TEMATICAS.map(t => <option key={t} value={t} />)}
            </datalist>
          </div>
          {/* P2.5 — vinculados (textarea, full-width). */}
          <div style={{ gridColumn: "span 2" }}>
            <label>Vinculados{req("vinculados")}</label>
            <textarea
              value={v.vinculados || ""}
              onChange={(e) => set("vinculados", e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder="Otros vinculados al proceso (uno por línea o separados por comas)"
              rows={2}
              style={fieldStyle("vinculados")}
            />
          </div>
          {/* feature/litisoft-completion — sprint87. cc_vinculados (optional,
              never required — not in LITISOFT_REQUIRED_FIELDS so no asterisk). */}
          <div style={{ gridColumn: "span 2" }}>
            <label>CC Vinculados</label>
            <textarea
              value={v.cc_vinculados || ""}
              onChange={(e) => set("cc_vinculados", e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder="Cédulas de los vinculados (opcional)"
              rows={2}
              style={fieldStyle("cc_vinculados")}
            />
          </div>

          {/* sprint83 — 3 new LITISOFT enum fields. Backend allows-list
              enforces values; if a value is somehow stored that isn't
              in the dropdown (legacy), we still show the raw value as
              the "current" option so it's not silently dropped. */}
          <div>
            <label>Pretensión{req("pretension")}</label>
            <select value={v.pretension || ""}
                    onChange={(e) => set("pretension", e.target.value || null)}
                    style={fieldStyle("pretension")}>
              <option value="">— Seleccionar —</option>
              {PRETENSION_VALUES.map((p) => <option key={p} value={p}>{p}</option>)}
              {v.pretension && !PRETENSION_VALUES.includes(v.pretension) && (
                <option value={v.pretension}>{v.pretension} (valor anterior)</option>
              )}
            </select>
          </div>
          <div>
            <label>Calidad de Parte Previsora{req("calidad_parte_previsora")}</label>
            <select value={v.calidad_parte_previsora || ""}
                    onChange={(e) => set("calidad_parte_previsora", e.target.value || null)}
                    style={fieldStyle("calidad_parte_previsora")}>
              <option value="">— Seleccionar —</option>
              {CALIDAD_PARTE_PREVISORA_VALUES.map((p) => <option key={p} value={p}>{p}</option>)}
              {v.calidad_parte_previsora && !CALIDAD_PARTE_PREVISORA_VALUES.includes(v.calidad_parte_previsora) && (
                <option value={v.calidad_parte_previsora}>{v.calidad_parte_previsora} (valor anterior)</option>
              )}
            </select>
          </div>
          <div>
            <label>Tipo de Notificación{req("tipo_notificacion")}</label>
            <select value={v.tipo_notificacion || ""}
                    onChange={(e) => set("tipo_notificacion", e.target.value || null)}
                    style={fieldStyle("tipo_notificacion")}>
              <option value="">— Seleccionar —</option>
              {TIPO_NOTIFICACION_VALUES.map((p) => <option key={p} value={p}>{p}</option>)}
              {v.tipo_notificacion && !TIPO_NOTIFICACION_VALUES.includes(v.tipo_notificacion) && (
                <option value={v.tipo_notificacion}>{v.tipo_notificacion} (valor anterior)</option>
              )}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export function pickLitisoftFields(payload) {
  const out = {};
  // P2.5 → sprint83: 13 fields total. Older callers passing only the
  // original 6 still work; later fields just don't appear in the
  // payload when undefined.
  for (const k of [
    "fecha_siniestro","placa","numero_siniestro","departamento","municipio","tematica",
    "poliza_soat","accionado_previsora","vinculados","vinculado_principal",
    "pretension","calidad_parte_previsora","tipo_notificacion",
  ]) {
    if (payload[k] !== undefined) out[k] = payload[k] === "" ? null : payload[k];
  }
  return out;
}
